import { CommonModule as AngularCommonModule } from '@angular/common';
import { Component, forwardRef, Input, type OnChanges, type OnInit, type SimpleChanges } from '@angular/core';
import { ContextMenuStateIndicatorComponent } from '@frontendlib/components/features/popover/components/context-menu-state-indicator/context-menu-state-indicator.component';
import { ExpandableListDirective } from '@frontendlib/components/directives/expandable-list.directive';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { SkeletonBlockComponent } from '@frontendlib/components/components/skeleton-block/skeleton-block.component';
import { PopoverDirective } from '@frontendlib/components/features/popover/directives/popover.directive';
import { BaseSearchResponse } from '@modules/common/types/base-search-response';
import { DragNDropModule } from '@modules/drag-n-drop/drag-n-drop.module';
import { ManageContextMenuDirective } from '@modules/knots/directives/manage-context-menu.directive';
import { Knot } from '@modules/knots/types/knot';
import { type KnotsManageListState } from '@modules/knots/types/knots-manage-list-state';
import { IncludesByPipe } from '@modules/utils/pipes/array/includes-by.pipe';
import type { StitchItemSummaryDto } from '@sharedlib/dtos/dto/StitchItemSummaryDto';
import { Observable } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { AddKnotsComponent } from '../add-knots/add-knots.component';
import { KnotComponent } from '../knot/knot.component';
import { KnotsComponent } from '../knots/knots.component';

@Component({
  selector: 'app-shared-knots',
  templateUrl: '../knots/knots.component.html',
  styleUrls: ['../knots/knots.component.less'],
  standalone: true,
  imports: [
    AngularCommonModule,
    KnotComponent,
    IconComponent,
    ContextMenuStateIndicatorComponent,
    PopoverDirective,
    ManageContextMenuDirective,
    ExpandableListDirective,
    IncludesByPipe,
    // Compiles KnotsComponent's template; the skeleton is never drawn on a shared page (`isShared`).
    SkeletonBlockComponent,
    // Compiles KnotsComponent's template; its drop predicate refuses every drop on a shared page (`isShared`).
    DragNDropModule,
    forwardRef(() => AddKnotsComponent),
  ],
})
export class SharedKnotsComponent extends KnotsComponent implements OnInit, OnChanges {
  @Input() public stitchItem: StitchItemSummaryDto;

  public ngOnInit(): void {
    // Initialize state if not set
    if (!this.state) {
      this.state = {} as KnotsManageListState;
    }

    // Wire loadPage against the shared endpoint (super.ngOnInit is skipped, so the parent's
    // page-fetch subscription doesn't run).
    this.loadPage
      .pipe(
        switchMap(() => this.searchKnots()),
        takeUntil(this.alive),
      )
      .subscribe(({ items, count }) => this.updateKnots(items, count));

    this.kpService
      .getSelectedKnots()
      .pipe(takeUntil(this.alive))
      .subscribe((knots: Knot[]) => {
        this.selectedKnots.set(knots);
      });
  }

  public override ngOnChanges(changes: SimpleChanges): void {
    // When stitchItem changes, trigger data loading
    if ('stitchItem' in changes && changes['stitchItem'].currentValue) {
      const currentId = changes['stitchItem'].currentValue?.id;
      const previousId = changes['stitchItem'].previousValue?.id;

      if (currentId !== previousId) {
        this.expanded.set(false);
        this.page.set(0);
        this.knots.set([]);
      }
      this.searchKnots()
        .pipe(takeUntil(this.alive))
        .subscribe(({ items, count }) => this.updateKnots(items, count));
    }
  }

  protected override searchKnots(): Observable<BaseSearchResponse<Knot>> {
    if (!this.stitchItem) {
      throw new Error('Stitch item is required for shared knots');
    }

    // Sorting/filtering is disabled on shared pages (applying a sort drops the markers — SBUX-539),
    // so ignore any persisted sort/filter state and always fetch in the backend default order.
    return this.knotsService.searchShared(this.stitchItem, {
      offset: this.page() * this.perPage,
      limit: this.perPage,
    });
  }
}
